<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

import Copilot from 'dashboard/components-next/copilot/Copilot.vue';
import WeastAPI from 'dashboard/api/weast';

defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const messages = ref([]);
const weastConversationId = ref('');
const isGenerating = ref(false);
let messageId = 0;

// Weast talks directly to the EasternStack AI API and has no Captain
// assistants, but the panel UI needs at least one assistant to render the
// input and quick prompts, so it gets a static placeholder.
const assistants = [{ id: 'weast', name: 'Weast' }];
const activeAssistant = assistants[0];

const shouldShowCopilotPanel = computed(
  () => uiSettings.value.is_copilot_panel_open
);

const closeCopilotPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const handleReset = () => {
  messages.value = [];
  weastConversationId.value = '';
};

const sendMessage = async message => {
  if (isGenerating.value) return;

  messageId += 1;
  messages.value = [
    ...messages.value,
    {
      id: `weast-user-${messageId}`,
      message_type: 'user',
      message: { content: message },
    },
  ];

  isGenerating.value = true;
  try {
    const { data } = await WeastAPI.chat(message, weastConversationId.value);
    weastConversationId.value =
      data.conversation_id || weastConversationId.value;
    messageId += 1;
    messages.value = [
      ...messages.value,
      {
        id: `weast-assistant-${messageId}`,
        message_type: 'assistant',
        message: { content: data.message, reply_suggestion: true },
      },
    ];
  } catch (error) {
    useAlert(error?.response?.data?.error || error.message);
  } finally {
    isGenerating.value = false;
  }
};
</script>

<template>
  <div
    v-if="shouldShowCopilotPanel"
    v-on-click-outside="() => closeCopilotPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowCopilotPanel,
        'md:hidden': !shouldShowCopilotPanel,
      },
    ]"
  >
    <Copilot
      :messages="messages"
      :conversation-inbox-type="conversationInboxType"
      :assistants="assistants"
      :active-assistant="activeAssistant"
      @send-message="sendMessage"
      @reset="handleReset"
    />
  </div>
  <template v-else />
</template>
